import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "redux/actions/auth";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import cosmaLogo from "assets/images/logos/Cosmaupload.png";
import { useMaterialUIController, setLayout } from "context";

const S = {
  root: {
    fontFamily: "'Inter', sans-serif",
    background: `url(${bgImage}) center center / cover no-repeat`,
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  box: {
    background: "#fff",
    border: "1px solid rgba(123,94,167,0.18)",
    borderRadius: "24px",
    padding: "48px 40px",
    boxShadow: "0 2px 20px rgba(123,94,167,0.10)",
    width: "100%",
    maxWidth: "460px",
  },
  logo: { marginBottom: "28px", textAlign: "center" },
  logoImg: { height: "44px" },
  h3: { fontSize: "1.6rem", fontWeight: 800, marginBottom: "8px", color: "#1a0a2e", textAlign: "center", margin: 0 },
  sub: { color: "#7a6895", fontSize: "0.9rem", marginBottom: "32px", textAlign: "center", marginTop: "8px" },
  fieldWrap: { marginBottom: "16px" },
  label: {
    display: "block", fontSize: "0.78rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".06em",
    color: "#7a6895", marginBottom: "6px",
  },
  input: {
    width: "100%", padding: "13px 16px", background: "#f3eeff",
    border: "1.5px solid rgba(123,94,167,0.18)", borderRadius: "10px",
    color: "#1a0a2e", fontSize: "0.95rem", fontFamily: "inherit",
    outline: "none", boxSizing: "border-box", transition: "border-color .2s",
  },
  inputFocus: { borderColor: "#7b5ea7" },
  btn: {
    width: "100%", padding: "15px", borderRadius: "10px", fontSize: "1rem",
    fontWeight: 800, cursor: "pointer", border: "none", marginTop: "8px",
    background: "linear-gradient(135deg,#7b5ea7,#9c27b0)", color: "#fff",
    boxShadow: "0 4px 20px rgba(123,94,167,0.35)", fontFamily: "inherit",
    transition: "all .25s",
  },
  divider: { border: "none", borderTop: "1px solid rgba(123,94,167,0.18)", margin: "24px 0" },
  ssoBtn: {
    width: "100%", padding: "12px", borderRadius: "10px", fontSize: "0.9rem",
    fontWeight: 600, cursor: "pointer", border: "1.5px solid rgba(123,94,167,0.18)",
    background: "transparent", color: "#1a0a2e", display: "flex",
    alignItems: "center", justifyContent: "center", gap: "10px",
    fontFamily: "inherit", transition: "all .2s",
  },
  links: { marginTop: "20px", fontSize: "0.83rem", color: "#7a6895", textAlign: "center" },
  a: { color: "#7b5ea7", textDecoration: "none" },
  toast: {
    position: "fixed", bottom: "32px", left: "50%", transform: "translateX(-50%)",
    background: "linear-gradient(135deg,#7b5ea7,#9c27b0)", color: "#fff",
    padding: "12px 28px", borderRadius: "100px", fontWeight: 700,
    fontSize: "0.9rem", zIndex: 9999, whiteSpace: "nowrap",
    transition: "opacity .3s",
  },
  communityBanner: {
    background: "rgba(123,94,167,0.08)",
    border: "1.5px solid rgba(123,94,167,0.35)",
    borderRadius: "12px",
    padding: "14px 18px",
    marginBottom: "20px",
    fontSize: "0.85rem",
    color: "#1a0a2e",
    lineHeight: 1.6,
  },
  upgradeLink: {
    color: "#7b5ea7",
    fontWeight: 700,
    textDecoration: "underline",
    cursor: "pointer",
  },
  playBtn: {
    position: "fixed", top: "24px", right: "28px",
    display: "flex", alignItems: "center", gap: "8px",
    background: "#fff", color: "#7b5ea7",
    border: "2px solid #7b5ea7",
    borderRadius: "100px", padding: "10px 20px",
    fontSize: "0.88rem", fontWeight: 800,
    cursor: "pointer", textDecoration: "none",
    fontFamily: "'Inter', sans-serif",
    boxShadow: "0 4px 18px rgba(123,94,167,0.22)",
    transition: "all .22s",
    zIndex: 1000,
  },
};

function Basic() {
  const dispatch = useDispatch();
  const [controller, uiDispatch] = useMaterialUIController();
  const { loginLoading, communityBlocked } = useSelector((state) => state.auth);

  useEffect(() => {
    setLayout(uiDispatch, "page");
  }, []);

  const [form, setForm] = useState({ email: "", password: "" });
  const [focusedField, setFocusedField] = useState(null);
  const [toast, setToast] = useState({ msg: "", show: false });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  function showToast(msg) {
    setToast({ msg, show: true });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    showToast("Signing in…");
    dispatch(actions.login(form));
  };

  const handleSSO = () => {
    showToast("Redirecting to SSO…");
    /* Connect Google / NHS OAuth here */
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <a
        href="https://play.cosmaapp.com"
        target="_blank"
        rel="noopener noreferrer"
        style={S.playBtn}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg,#7b5ea7,#9c27b0)";
          e.currentTarget.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#fff";
          e.currentTarget.style.color = "#7b5ea7";
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
        Play COSMA
      </a>
      <div style={S.root}>
        <div style={S.box}>
          <div style={S.logo}>
            <img src={cosmaLogo} alt="COSMA" style={S.logoImg} />
          </div>

          <h3 style={S.h3}>Sign In</h3>
          <p style={S.sub}>Access your portal, progress reports &amp; activities.</p>

          {communityBlocked && (
            <div style={S.communityBanner}>
              Community users can access COSMA games but not the portal. Please use the COSMA game app or{" "}
              <a
                href="https://buy.stripe.com/28EcN4axfdNN6ZQ1B8gMw09"
                target="_blank"
                rel="noopener noreferrer"
                style={S.upgradeLink}
              >
                Upgrade to Premium
              </a>
              .
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={S.fieldWrap}>
              <label style={S.label}>Email</label>
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                required
                value={form.email}
                onChange={handleChange}
                style={{ ...S.input, ...(focusedField === "email" ? S.inputFocus : {}) }}
                onFocus={() => setFocusedField("email")}
                onBlur={() => setFocusedField(null)}
              />
            </div>
            <div style={S.fieldWrap}>
              <label style={S.label}>Password</label>
              <input
                type="password"
                name="password"
                placeholder="••••••••"
                required
                value={form.password}
                onChange={handleChange}
                style={{ ...S.input, ...(focusedField === "password" ? S.inputFocus : {}) }}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
              />
            </div>
            <button type="submit" disabled={loginLoading} style={S.btn}>
              {loginLoading ? "Signing in…" : "Sign In to Portal →"}
            </button>
          </form>

          <hr style={S.divider} />

          <button style={S.ssoBtn} onClick={handleSSO}>
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path
                fill="#4285F4"
                d="M44.5 20H24v8.5h11.7C34.2 33.5 29.7 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l6.3-6.3C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.2-4z"
              />
            </svg>
            Sign in with Google / NHS SSO
          </button>

          <p style={S.links}>
            No account?{" "}
            <a href="https://cosmaapp.com/#pricing" style={S.a}>
              Start free trial →
            </a>
            &nbsp;|&nbsp;
            <a href="/auth/register" style={S.a}>
              Register Here
            </a>
          </p>
        </div>
      </div>

      {toast.show && (
        <div style={{ ...S.toast, opacity: 1 }}>{toast.msg}</div>
      )}
    </>
  );
}

export default Basic;
